import { Component, onMount, createMemo } from "solid-js";
import type { LexicalRichText } from "~/types/lexicalrichtext";
import { renderLexicalContent, type RichTextStyles } from "./renderLexicalContent";
import { initializeEmbedConsent, refreshEmbedConsent } from "~/utils/consent/embedConsentHandler";
import styles from "./RichTextRenderer.module.css";

export type RichTextVariant = "default" | "lightOnDark" | "onAccent" | "hero" | "compact";

export interface RichTextRendererProps {
  /** Lexical rich text JSON content from CMS */
  content: LexicalRichText | undefined;
  /** Visual variant for different contexts */
  variant?: RichTextVariant;
  /** Additional CSS classes */
  class?: string;
}

/**
 * RichTextRenderer Component
 *
 * Renders Lexical rich text content with scoped CSS Module styles.
 * Centralizes consent handling for social media embeds.
 *
 * Usage:
 * ```tsx
 * <RichTextRenderer content={richTextJson} variant="lightOnDark" />
 * ```
 *
 * Variants:
 * - `default` - Theme-aware text (dark on light theme, light on dark theme)
 * - `lightOnDark` - Light text for dark backgrounds (theme-aware)
 * - `onAccent` - Fixed light text for colored backgrounds (station colors, CMS colors)
 * - `hero` - Larger typography for hero sections
 * - `compact` - Tighter spacing for cards
 *
 * Note: Ensure `initializeEmbedConsent()` is called once at app startup
 * to set up the document-level click handler for consent buttons.
 */
export const RichTextRenderer: Component<RichTextRendererProps> = (props) => {
  // Map CSS Module classes to RichTextStyles interface
  const richTextStyles: RichTextStyles = {
    // Typography
    h1: styles.h1,
    h2: styles.h2,
    h3: styles.h3,
    h4: styles.h4,
    h5: styles.h5,
    h6: styles.h6,
    p: styles.p,
    strong: styles.strong,
    em: styles.em,
    u: styles.u,
    s: styles.s,
    code: styles.code,
    sub: styles.sub,
    sup: styles.sup,
    // Lists
    ul: styles.ul,
    ol: styles.ol,
    li: styles.li,
    // Links
    a: styles.a,
    // Media
    img: styles.img,
    imgSmall: styles.imgSmall,
    imgMedium: styles.imgMedium,
    imgLarge: styles.imgLarge,
    imgFull: styles.imgFull,
    figure: styles.figure,
    figcaption: styles.figcaption,
    // Blocks
    blockquote: styles.blockquote,
    pre: styles.pre,
  };

  // Memoize the rendered HTML to prevent unnecessary re-renders
  const renderedContent = createMemo(() => renderLexicalContent(props.content, richTextStyles));

  // Set up consent click handler (idempotent) and check existing consent
  onMount(() => {
    initializeEmbedConsent();
    refreshEmbedConsent();
  });

  return (
    <div
      class={styles.container}
      classList={{
        [styles.lightOnDark]: props.variant === "lightOnDark",
        [styles.onAccent]: props.variant === "onAccent",
        [styles.hero]: props.variant === "hero",
        [styles.compact]: props.variant === "compact",
        [props.class || ""]: !!props.class,
      }}
      innerHTML={renderedContent()}
    />
  );
};

export default RichTextRenderer;
